// Header component for the application top bar
import React, { useState, useRef, useEffect } from 'react';
import { IconButton, Headline } from '@yaradigitallabs/ahua-react';
import logo from '@/assets/logo.svg';
import { UserProfile } from './UserProfile';

// Props for Header component
// title: optional page title
// signOut: callback for user sign out
type HeaderProps = {
  title?: string;
  signOut: () => void;
};

// Header renders the top navigation bar with logo, title, and action icons
export const Header: React.FC<HeaderProps> = ({ title, signOut }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Handler for notification/info icon click
  const handleNotificationClick = () => {
    // console.log('Notification clicked');
  };

  // Handler for profile icon click (toggles dropdown)
  const handleProfileClick = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Effect to close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    // Fixed header bar with logo, title, and action icons
    <div className="bg-[#102D6A] border-b border-gray-200 fixed top-0 left-0 w-full z-50 shadow-md">
      <div className="flex items-center pl-2 pr-4 w-full gap-4">
        {/* Left Section - Yara Logo */}
        <div className="flex items-center p-4">
          <img src={logo} alt="Yara Logo" className="h-6 w-6" />
        </div>

        <div className="flex gap-4 justify-between items-center w-full">
          {/* Center Section - Title Bar Container */}
          <Headline
            size="2xs"
            css={{ color: 'white', letterSpacing: '0.01em' }}
          >
            {title}
          </Headline>

          {/* Right Section - Action Icons */}
          <div className="flex items-center gap-2">
            {/* Information Icon */}
            <IconButton
              variant="ghost"
              size="xs"
              onClick={handleNotificationClick}
              className="p-3 bg-none text-white"
              title="Information"
              colorConcept="inverse"
              icon="Info"
            />

            {/* Profile Icon with Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={handleProfileClick}
                className="p-3 bg-none text-white"
                title="User Profile"
                colorConcept="inverse"
                icon="Profile"
              />

              {/* Dropdown Menu for user profile actions */}
              {isDropdownOpen && <UserProfile signOut={signOut} />}
            </div>

            {/* Application Icon */}
            <IconButton
              variant="ghost"
              size="xs"
              onClick={handleNotificationClick}
              className="p-3 bg-none text-white"
              title="Applications"
              colorConcept="inverse"
              icon="Bento"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
